/**
 * Generator Store Actions
 * All generator state mutations go through here
 *
 * Part of Phase 2: State Management Consolidation
 * @version 1.0.0
 */

// Import Store - will be available after store.js loads
// Using dynamic import pattern to avoid circular dependencies
let Store = null;

function getStore() {
  if (!Store) {
    Store = window.Store;
  }
  if (!Store) {
    console.warn('[GeneratorActions] Store not available yet');
  }
  return Store;
}

/**
 * Safe store access - returns false if Store not ready
 */
function safeSet(path, value) {
  const store = getStore();
  if (store) {
    store.set(path, value);
    return true;
  }
  return false;
}

function safeGet(path, defaultValue) {
  const store = getStore();
  return store ? store.get(path) : defaultValue;
}

function safeUpdate(path, partial) {
  const store = getStore();
  if (store) {
    store.update(path, partial);
    return true;
  }
  return false;
}

/**
 * Set current phase (1-4)
 * @param {number} phase
 */
export function setPhase(phase) {
  if (phase < 1 || phase > 4) {
    console.error(`[GeneratorActions] Invalid phase: ${phase}`);
    return;
  }
  safeSet('generator.phase', phase);
}

/**
 * Update brief
 * @param {Object} brief - { product, benefit, formats, productId, etc. }
 */
export function setBrief(brief) {
  safeSet('generator.brief', {
    product: brief.product || '',
    benefit: brief.benefit || '',
    audience: brief.audience || '',
    tone: brief.tone || '',
    // Include formats and product data in brief for backwards compatibility
    formats: brief.formats || [],
    productId: brief.productId || null,
    productData: brief.productData || null
  });
  // Also set formats separately for components that read from generator.formats
  if (brief.formats) {
    safeSet('generator.formats', brief.formats);
  }
}

/**
 * Update brief partially (merge)
 * @param {Object} partial - Partial brief object
 */
export function updateBrief(partial) {
  safeUpdate('generator.brief', partial);
}

/**
 * Set selected formats
 * @param {string[]} formats
 */
export function setFormats(formats) {
  safeSet('generator.formats', Array.isArray(formats) ? formats : []);
}

/**
 * Add format to selection
 * @param {string} format
 */
export function addFormat(format) {
  const store = getStore();
  console.log('[GeneratorActions] addFormat called:', format, 'Store:', !!store);
  const current = safeGet('generator.formats', []);
  console.log('[GeneratorActions] Current formats:', current);
  if (!current.includes(format)) {
    const result = safeSet('generator.formats', [...current, format]);
    console.log('[GeneratorActions] safeSet result:', result);
    console.log('[GeneratorActions] New formats:', safeGet('generator.formats', []));
  }
}

/**
 * Remove format from selection
 * @param {string} format
 */
export function removeFormat(format) {
  const current = safeGet('generator.formats', []);
  safeSet('generator.formats', current.filter(f => f !== format));
}

/**
 * Toggle format selection
 * @param {string} format
 */
export function toggleFormat(format) {
  const current = safeGet('generator.formats', []);
  if (current.includes(format)) {
    removeFormat(format);
  } else {
    addFormat(format);
  }
}

/**
 * Set selected triggers
 * @param {string[]} triggers
 */
export function setTriggers(triggers) {
  safeSet('generator.triggers', Array.isArray(triggers) ? triggers : []);
}

/**
 * Add trigger to selection
 * @param {string} triggerCode
 */
export function addTrigger(triggerCode) {
  const current = safeGet('generator.triggers', []);
  if (!current.includes(triggerCode)) {
    safeSet('generator.triggers', [...current, triggerCode]);
  }
}

/**
 * Remove trigger from selection
 * @param {string} triggerCode
 */
export function removeTrigger(triggerCode) {
  const current = safeGet('generator.triggers', []);
  safeSet('generator.triggers', current.filter(t => t !== triggerCode));
}

/**
 * Toggle trigger selection
 * @param {string} triggerCode
 */
export function toggleTrigger(triggerCode) {
  const current = safeGet('generator.triggers', []);
  if (current.includes(triggerCode)) {
    removeTrigger(triggerCode);
  } else {
    addTrigger(triggerCode);
  }
}

/**
 * Set generated ideas
 * @param {import('../../core/models/idea.js').Idea[]} ideas
 */
export function setIdeas(ideas) {
  safeSet('generator.ideas', Array.isArray(ideas) ? ideas : []);
}

/**
 * Add idea to generated ideas
 * @param {import('../../core/models/idea.js').Idea} idea
 */
export function addIdea(idea) {
  const current = safeGet('generator.ideas', []);
  safeSet('generator.ideas', [...current, idea]);
}

/**
 * Update specific idea
 * @param {string} ideaId
 * @param {Partial<import('../../core/models/idea.js').Idea>} updates
 */
export function updateIdea(ideaId, updates) {
  const ideas = safeGet('generator.ideas', []);
  const index = ideas.findIndex(i => i.id === ideaId);
  if (index !== -1) {
    const updated = { ...ideas[index], ...updates };
    const newIdeas = [...ideas];
    newIdeas[index] = updated;
    safeSet('generator.ideas', newIdeas);
  }
}

/**
 * Remove idea from generator
 * @param {string} ideaId
 */
export function removeIdea(ideaId) {
  const ideas = safeGet('generator.ideas', []);
  safeSet('generator.ideas', ideas.filter(i => i.id !== ideaId));
}

/**
 * Set current idea for canvas view
 * @param {string|null} ideaId
 */
export function setCurrentIdea(ideaId) {
  safeSet('generator.currentIdeaId', ideaId);
}

/**
 * Set generation in progress state
 * @param {boolean} isGenerating
 */
export function setGenerating(isGenerating) {
  safeSet('generator.isGenerating', isGenerating);
}

/**
 * Set loading state
 * @param {boolean} loading
 */
export function setLoading(loading) {
  safeSet('generator.loading', loading);
}

/**
 * Set error state
 * @param {string|null} error
 */
export function setError(error) {
  safeSet('generator.error', error);
}

/**
 * Update generation progress
 * @param {Object} progress - { current, total, currentFormat, currentTrigger }
 */
export function setGenerationProgress(progress) {
  safeSet('generator.generationProgress', progress);
}

/**
 * Load brand cartridge
 * @param {Object|null} brand
 */
export function setBrand(brand) {
  safeSet('generator.brand', brand);
}

/**
 * Set session ID for current generation
 * @param {string|null} sessionId
 */
export function setSessionId(sessionId) {
  safeSet('generator.sessionId', sessionId);
}

/**
 * Reset generator to initial state
 */
export function resetGenerator() {
  safeSet('generator', {
    phase: 1,
    brief: { product: '', benefit: '', audience: '', tone: '' },
    brand: null,
    formats: [],
    triggers: [],
    ideas: [],
    currentIdeaId: null,
    isGenerating: false,
    loading: false,
    generationProgress: { current: 0, total: 0, currentFormat: null, currentTrigger: null },
    sessionId: null,
    error: null
  });
}

/**
 * Initialize actions - register with Store
 * Call this after Store is available
 */
export function initActions() {
  const store = getStore();
  if (!store) {
    console.warn('[GeneratorActions] Store not available yet');
    return;
  }

  // Register actions with Store for dispatch pattern
  store.registerAction('generator/setPhase', setPhase);
  store.registerAction('generator/setBrief', setBrief);
  store.registerAction('generator/updateBrief', updateBrief);
  store.registerAction('generator/setFormats', setFormats);
  store.registerAction('generator/addFormat', addFormat);
  store.registerAction('generator/removeFormat', removeFormat);
  store.registerAction('generator/toggleFormat', toggleFormat);
  store.registerAction('generator/setTriggers', setTriggers);
  store.registerAction('generator/addTrigger', addTrigger);
  store.registerAction('generator/removeTrigger', removeTrigger);
  store.registerAction('generator/toggleTrigger', toggleTrigger);
  store.registerAction('generator/setIdeas', setIdeas);
  store.registerAction('generator/addIdea', addIdea);
  store.registerAction('generator/updateIdea', updateIdea);
  store.registerAction('generator/removeIdea', removeIdea);
  store.registerAction('generator/setCurrentIdea', setCurrentIdea);
  store.registerAction('generator/setGenerating', setGenerating);
  store.registerAction('generator/setLoading', setLoading);
  store.registerAction('generator/setError', setError);
  store.registerAction('generator/setGenerationProgress', setGenerationProgress);
  store.registerAction('generator/setBrand', setBrand);
  store.registerAction('generator/setSessionId', setSessionId);
  store.registerAction('generator/reset', resetGenerator);

  console.log('[GeneratorActions] Registered with Store');
}

// Export all actions as default object
export default {
  setPhase,
  setBrief,
  updateBrief,
  setFormats,
  addFormat,
  removeFormat,
  toggleFormat,
  setTriggers,
  addTrigger,
  removeTrigger,
  toggleTrigger,
  setIdeas,
  addIdea,
  updateIdea,
  removeIdea,
  setCurrentIdea,
  setGenerating,
  setLoading,
  setError,
  setGenerationProgress,
  setBrand,
  setSessionId,
  resetGenerator,
  initActions
};
